/**
 * Step 2: Countries — per-country overrides + static tag propagation + info banner.
 */

import { Globe, Info } from 'lucide-react';

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

import { PerCountryOverridesPanel } from '../config/PerCountryOverridesPanel';
import { StaticTagPropagation } from '../config/static-tag-propagation';
import { useStaticTagPropagation } from '../hooks/useStaticTagPropagation';
import type { StaticTagPropagationMatchKind } from '../hooks/staticTagPropagation.types';
import type { useMultiCountryBulkEditForm } from '../hooks';
import type { useMultiCountrySentScheduledEditForm } from '@/features/smswiser-campaigns/components/MultiCountrySentScheduledEdit/hooks/useMultiCountrySentScheduledEditForm';

type Form =
  | ReturnType<typeof useMultiCountryBulkEditForm>
  | ReturnType<typeof useMultiCountrySentScheduledEditForm>;

interface CatalogForCountry {
  gateways: Array<{ id: number; name: string; pricePerSms?: number }>;
  campaigns: Array<{ id: number; name: string }>;
  tlds: Array<{ value: string; displayName: string }>;
  leadsLists?: Array<{ id: number; name: string }>;
}

interface Props {
  form: Form;
  selectedIds: string[];
  mergedDrops: PreparedDrop[];
  catalogByCountry: Record<string, CatalogForCountry>;
  sourceCountryKey: string;
  affectedDraftCount: number;
  isBusy: boolean;
  propagationMatchKind?: StaticTagPropagationMatchKind;
  /** Banner noun after count, e.g. "drafts" (default) or "scheduled drops". */
  affectedBannerNoun?: string;
  staticTagMatchCountUnit?: { singular: string; plural: string };
  staticTagPropagationDescription?: string;
}

export function CountriesStep(props: Props) {
  const {
    form,
    selectedIds,
    mergedDrops,
    catalogByCountry,
    sourceCountryKey,
    affectedDraftCount,
    isBusy,
    propagationMatchKind = 'draft',
    affectedBannerNoun = 'drafts',
    staticTagMatchCountUnit,
    staticTagPropagationDescription,
  } = props;

  const propagation = useStaticTagPropagation({
    selectedIds,
    allDrops: mergedDrops,
    sourceCountryKey,
    matchKind: propagationMatchKind,
  });

  const handleTogglePropagation = (ck: string) => {
    form.setPropagateToCountries((prev) =>
      prev.includes(ck) ? prev.filter((k) => k !== ck) : [...prev, ck],
    );
  };

  const countryCount = form.allCountryKeys.length;

  return (
    <div className="flex flex-col gap-5 p-5">
      <div className="flex items-center gap-3 rounded-lg border border-blue-500/20 bg-blue-500/5 px-4 py-3">
        <Info className="h-4 w-4 shrink-0 text-blue-500" />
        <div className="flex items-center gap-2 text-sm text-blue-600">
          <Globe className="h-3.5 w-3.5" />
          <span>
            <span className="font-semibold">
              {affectedDraftCount} {affectedBannerNoun}
            </span>{' '}
            across{' '}
            <span className="font-semibold">{countryCount} countr{countryCount !== 1 ? 'ies' : 'y'}</span>
          </span>
        </div>
      </div>

      <PerCountryOverridesPanel
        countryKeys={form.allCountryKeys}
        overrides={form.overrides}
        onOverride={form.setCountryOverride}
        catalogByCountry={catalogByCountry}
        propagatedCountries={form.propagateToCountries}
        disabled={isBusy}
      />

      {propagation.hasMatches && (
        <StaticTagPropagation
          groups={propagation.countryGroups}
          staticTagColors={propagation.staticTagColorByName}
          totalMatchCount={propagation.totalMatchCount}
          matchCountUnit={staticTagMatchCountUnit}
          description={staticTagPropagationDescription}
          selectedCountries={form.propagateToCountries}
          onToggle={handleTogglePropagation}
          onSelectAll={() =>
            form.setPropagateToCountries(
              propagation.countryGroups.filter((g) => !g.isBulkSourceCountry).map((g) => g.countryKey),
            )
          }
          onDeselectAll={() => form.setPropagateToCountries([])}
          onAddCountries={(keys) =>
            form.setPropagateToCountries((prev) => [...new Set([...prev, ...keys])])
          }
          onRemoveCountries={(keys) =>
            form.setPropagateToCountries((prev) => prev.filter((k) => !keys.includes(k)))
          }
          disabled={isBusy}
        />
      )}
    </div>
  );
}
